/**
 * Leviathan Web - Truck Detail Page
 * Quasar Prime LLC
 * View and edit individual truck details
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import TruckForm from '@components/trucks/TruckForm';
import TruckComplianceSection from '@components/trucks/TruckComplianceSection';
import TruckTerminalRegistrations from '@components/trucks/TruckTerminalRegistrations';
import { API_URL } from '@/config/api';

interface Truck {
  id: string;
  unitNumber: string;
  name: string | null;
  vin: string | null;
  licensePlate: string | null;
  state: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  color: string | null;
  totalCapacity: number | null;
  compartments: number | null;
  compartmentConfig: string | null;
  status: string;
  currentDriverId: string | null;
  currentLocation: string | null;
  lastServiceDate: string | null;
  nextServiceDue: string | null;
  odometerReading: number | null;
  dotInspectionDate: string | null;
  dotInspectionDue: string | null;
  registrationExpiry: string | null;
  insuranceExpiry: string | null;
  insuranceCarrier: string | null;
  insurancePolicyNumber: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Format date for display
 */
function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format date for form input (YYYY-MM-DD)
 */
function formatDateForInput(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().split('T')[0] ?? '';
}

/**
 * Check if date is within 30 days or past
 */
function isDateWarning(dateStr: string | null): 'critical' | 'warning' | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const today = new Date();
  const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

  if (date < today) return 'critical';
  if (date <= thirtyDays) return 'warning';
  return null;
}

/**
 * Get status styling
 */
function getStatusStyle(status: string): string {
  switch (status) {
    case 'AVAILABLE':
      return 'text-success-green bg-success-green/10';
    case 'IN_USE':
      return 'text-amber bg-amber/10';
    case 'MAINTENANCE':
      return 'text-warning-amber bg-warning-amber/10';
    case 'OUT_OF_SERVICE':
      return 'text-error-red bg-error-red/10';
    default:
      return 'text-text-secondary bg-text-secondary/10';
  }
}

const TruckDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [truck, setTruck] = useState<Truck | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'compliance' | 'registrations'>('details');

  useEffect(() => {
    fetchTruck();
  }, [id]);

  const fetchTruck = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/trucks/${id}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTruck(data.truck);
      } else if (response.status === 404) {
        navigate('/trucks');
      }
    } catch (error) {
      console.error('Failed to fetch truck:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;

    try {
      const response = await fetch(`${API_URL}/api/trucks/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          status: newStatus,
          currentLocation: newLocation || null,
        }),
      });

      if (response.ok) {
        setShowStatusModal(false);
        setNewStatus('');
        setNewLocation('');
        fetchTruck();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trucks/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        navigate('/trucks');
      }
    } catch (error) {
      console.error('Failed to delete truck:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="p-12 text-center">
        <div className="text-text-tertiary font-mono animate-pulse">
          &gt; Loading truck data...
        </div>
      </div>
    );
  }

  if (!truck) {
    return (
      <div className="p-12 text-center">
        <div className="text-text-tertiary font-mono">&gt; Truck not found</div>
      </div>
    );
  }

  // Prepare initial data for edit form
  const initialFormData = {
    unitNumber: truck.unitNumber,
    name: truck.name || '',
    vin: truck.vin || '',
    licensePlate: truck.licensePlate || '',
    state: truck.state || '',
    year: truck.year?.toString() || '',
    make: truck.make || '',
    model: truck.model || '',
    color: truck.color || '',
    totalCapacity: truck.totalCapacity?.toString() || '',
    compartments: truck.compartments?.toString() || '',
    compartmentConfig: truck.compartmentConfig || '',
    status: truck.status as 'AVAILABLE' | 'IN_USE' | 'MAINTENANCE' | 'OUT_OF_SERVICE',
    odometerReading: truck.odometerReading?.toString() || '',
    lastServiceDate: formatDateForInput(truck.lastServiceDate),
    nextServiceDue: formatDateForInput(truck.nextServiceDue),
    dotInspectionDate: formatDateForInput(truck.dotInspectionDate),
    dotInspectionDue: formatDateForInput(truck.dotInspectionDue),
    registrationExpiry: formatDateForInput(truck.registrationExpiry),
    insuranceExpiry: formatDateForInput(truck.insuranceExpiry),
    insuranceCarrier: truck.insuranceCarrier || '',
    insurancePolicyNumber: truck.insurancePolicyNumber || '',
    notes: truck.notes || '',
  };

  if (isEditMode) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
          <div className="border-l-4 border-amber pl-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <h1
                  className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                  style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
                >
                  Edit {truck.unitNumber}
                </h1>
                <button
                  onClick={() => setIsEditMode(false)}
                  className="text-text-secondary text-sm font-mono hover:text-amber transition-colors mt-2"
                >
                  &larr; Cancel Edit
                </button>
              </div>
            </div>
          </div>
        </div>

        <TruckForm
          truckId={id}
          initialData={initialFormData}
          onSuccess={() => {
            setIsEditMode(false);
            fetchTruck();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-4">
                <span className="text-4xl">
                  {truck.status === 'IN_USE' ? '🚛' : truck.status === 'MAINTENANCE' ? '🔧' : '🚚'}
                </span>
                <div>
                  <h1
                    className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                    style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
                  >
                    {truck.unitNumber}
                  </h1>
                  {truck.name && (
                    <p className="text-text-secondary text-sm font-mono mt-1">"{truck.name}"</p>
                  )}
                </div>
              </div>
              <Link
                to="/trucks"
                className="text-text-secondary text-sm font-mono hover:text-amber transition-colors mt-2 inline-block"
              >
                &larr; Back to Fleet
              </Link>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowStatusModal(true)}
                className="bg-steel/20 hover:bg-steel/30 text-steel font-mono py-2 px-4 transition-all uppercase text-sm tracking-wider border border-steel/30 hover:border-steel"
              >
                Update Status
              </button>
              <button
                onClick={() => setIsEditMode(true)}
                className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-2 px-6 transition-all uppercase tracking-wider"
                style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Status Badge */}
      <div className="flex items-center space-x-4">
        <span
          className={`${getStatusStyle(truck.status)} font-mono text-sm uppercase tracking-wider font-semibold px-4 py-2 rounded`}
        >
          {truck.status.replace('_', ' ')}
        </span>
        {truck.currentLocation && truck.status === 'IN_USE' && (
          <span className="text-text-tertiary font-mono text-sm">
            Location: {truck.currentLocation}
          </span>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center space-x-1 border-b border-panel-border">
        {(['details', 'compliance', 'registrations'] as const).map((tab) => {
          const labels = {
            details: 'Details',
            compliance: 'Compliance',
            registrations: 'Terminal Registrations',
          };
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all border-b-2 -mb-px ${
                isActive
                  ? 'text-amber border-amber bg-amber/5'
                  : 'text-text-tertiary border-transparent hover:text-text-secondary hover:border-steel/30'
              }`}
            >
              {labels[tab]}
            </button>
          );
        })}
      </div>

      {/* Details Tab */}
      {activeTab === 'details' && (
        <>
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Vehicle Information */}
            <DetailSection title="Vehicle Information">
              <DetailRow label="VIN" value={truck.vin || '-'} />
              <DetailRow
                label="Vehicle"
                value={[truck.year, truck.make, truck.model].filter(Boolean).join(' ') || '-'}
              />
              <DetailRow label="Color" value={truck.color || '-'} />
              <DetailRow
                label="License Plate"
                value={
                  truck.licensePlate
                    ? `${truck.licensePlate}${truck.state ? ` (${truck.state})` : ''}`
                    : '-'
                }
              />
              <DetailRow
                label="Odometer"
                value={truck.odometerReading ? `${truck.odometerReading.toLocaleString()} miles` : '-'}
              />
            </DetailSection>

            {/* Capacity */}
            <DetailSection title="Capacity & Configuration">
              <DetailRow
                label="Total Capacity"
                value={truck.totalCapacity ? `${truck.totalCapacity.toLocaleString()} gallons` : '-'}
                highlight
              />
              <DetailRow
                label="Compartments"
                value={truck.compartments ? `${truck.compartments} compartments` : '-'}
              />
              <DetailRow label="Configuration" value={truck.compartmentConfig || '-'} />
            </DetailSection>

            {/* Maintenance */}
            <DetailSection title="Maintenance">
              <DetailRow label="Last Service" value={formatDate(truck.lastServiceDate)} />
              <DetailRow
                label="Next Service Due"
                value={formatDate(truck.nextServiceDue)}
                warning={isDateWarning(truck.nextServiceDue)}
              />
              <DetailRow label="Last DOT Inspection" value={formatDate(truck.dotInspectionDate)} />
              <DetailRow
                label="DOT Inspection Due"
                value={formatDate(truck.dotInspectionDue)}
                warning={isDateWarning(truck.dotInspectionDue)}
              />
            </DetailSection>

            {/* Compliance Summary */}
            <DetailSection title="Compliance Summary">
              <DetailRow
                label="Registration Expiry"
                value={formatDate(truck.registrationExpiry)}
                warning={isDateWarning(truck.registrationExpiry)}
              />
              <DetailRow
                label="Insurance Expiry"
                value={formatDate(truck.insuranceExpiry)}
                warning={isDateWarning(truck.insuranceExpiry)}
              />
              <DetailRow
                label="Insurance Carrier"
                value={truck.insuranceCarrier || '-'}
              />
              <DetailRow
                label="Policy Number"
                value={truck.insurancePolicyNumber || '-'}
              />
            </DetailSection>
          </div>

          {/* Notes */}
          {truck.notes && (
            <DetailSection title="Notes">
              <p className="text-text-secondary font-mono text-sm whitespace-pre-wrap">{truck.notes}</p>
            </DetailSection>
          )}

          {/* Metadata */}
          <div className="text-text-tertiary font-mono text-xs space-y-1">
            <p>Created: {formatDate(truck.createdAt)}</p>
            <p>Updated: {formatDate(truck.updatedAt)}</p>
          </div>
        </>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && id && (
        <TruckComplianceSection truckId={id} />
      )}

      {/* Terminal Registrations Tab */}
      {activeTab === 'registrations' && id && (
        <TruckTerminalRegistrations truckId={id} />
      )}

      {/* Delete Button */}
      <div className="pt-4 border-t border-panel-border">
        <button
          onClick={() => setShowDeleteModal(true)}
          className="text-error-red hover:text-error-red/80 font-mono text-sm uppercase tracking-wider transition-colors"
        >
          Deactivate Truck
        </button>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
          <div className="relative stat-card max-w-md w-full mx-4">
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

            <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
              <h3 className="text-xl font-display text-text-primary uppercase tracking-wide mb-6">
                Update Status
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    New Status
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="form-input w-full"
                  >
                    <option value="">-- Select Status --</option>
                    <option value="AVAILABLE">Available</option>
                    <option value="IN_USE">In Use</option>
                    <option value="MAINTENANCE">Maintenance</option>
                    <option value="OUT_OF_SERVICE">Out of Service</option>
                  </select>
                </div>

                {newStatus === 'IN_USE' && (
                  <div>
                    <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                      Current Location
                    </label>
                    <input
                      type="text"
                      value={newLocation}
                      onChange={(e) => setNewLocation(e.target.value)}
                      placeholder="e.g., En route to Customer A"
                      className="form-input w-full"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setNewStatus('');
                    setNewLocation('');
                  }}
                  className="px-4 py-2 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  disabled={!newStatus}
                  className="px-4 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
          <div className="relative stat-card max-w-md w-full mx-4">
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-error-red" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-error-red" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-error-red" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-error-red" />

            <div className="bg-panel-dark border border-error-red/50 p-6 clip-path-octagon">
              <h3 className="text-xl font-display text-error-red uppercase tracking-wide mb-4">
                Confirm Deactivation
              </h3>
              <p className="text-text-secondary font-mono text-sm mb-6">
                Are you sure you want to deactivate truck{' '}
                <span className="text-text-primary">{truck.unitNumber}</span>? This will remove it
                from active fleet operations.
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-error-red hover:bg-error-red/90 text-white font-display font-bold uppercase tracking-wider transition-all"
                >
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-4">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">{title}</h3>
        </div>
        <div className="space-y-3">{children}</div>
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  highlight,
  warning,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  warning?: 'warning' | 'critical' | null;
}) {
  const valueClass = highlight
    ? 'text-amber font-semibold'
    : warning === 'critical'
      ? 'text-error-red'
      : warning === 'warning'
        ? 'text-warning-amber'
        : 'text-text-primary';

  return (
    <div className="flex justify-between items-center">
      <span className="text-text-tertiary font-mono text-sm uppercase tracking-widest">{label}</span>
      <span className={`font-mono text-sm ${valueClass}`}>
        {value}
        {warning && ' !'}
      </span>
    </div>
  );
}

export default TruckDetailPage;
